import graphql__4ab68328b12bbda94f1d from "./__generated__/SettlementList_query.graphql.ts";import * as React from "react"
import {useEffect, useTransition} from "react"
import {graphql} from "react-relay";
import {usePaginationFragment} from "react-relay/hooks";
import LoadMoreButton from "./presentational/LoadMoreButton";
import {FormattedDate, useIntl} from "react-intl";
import {useNavigate} from "react-router";
import StatusBadge from "./presentational/StatusBadge";
import TableHeader from "./presentational/table/TableHeader";
import TableData, {TextAlignment} from "./presentational/table/TableData";
import Dinero from "dinero.js";
import Table from "./presentational/table/Table";

type Props = {
    queryRef: any
    searchTerm?: string
    onLoadingChange?: (isLoading: boolean) => void
}

type SettlementListProps = Props;

const SettlementList: React.FC<SettlementListProps> = (props) => {
    const intl = useIntl();
    const navigate = useNavigate()
    const [isPending, startTransition] = useTransition();

    const getSettlementStatusLabel = (status: string): string => {
        switch (status) {
            case 'OPEN':
                return intl.formatMessage({id: 'header.settlement.status.open'});
            case 'FINALIZED':
                return intl.formatMessage({id: 'header.settlement.status.finalized'});
            case 'INVOICED':
                return intl.formatMessage({id: 'header.settlement.status.invoiced'});
            case 'CLOSED':
                return intl.formatMessage({id: 'header.settlement.status.closed'});
            default:
                return intl.formatMessage({id: 'header.settlement.status.unknown'});
        }
    };

    const {data, hasNext, loadNext, isLoadingNext, refetch} = usePaginationFragment(
        graphql__4ab68328b12bbda94f1d,
        props.queryRef
    );

    useEffect(() => {
        // Use startTransition to keep the old data visible while loading new data
        // This prevents the Suspense fallback from showing
        startTransition(() => {
            refetch({searchTerm: props.searchTerm}, {fetchPolicy: 'store-and-network'});
        });
    }, [props.searchTerm, refetch])

    useEffect(() => {
        // Notify parent component about loading state
        props.onLoadingChange?.(isPending);
    }, [isPending, props]);

    if (!data.settlements || data.settlements.totalCount === 0) {
        return (<></>);
    }

    return (
        <nav className="flex-1 min-h-0 relative overflow-y-auto" aria-label={intl.formatMessage({id: 'navigation.settlements'})}>
            <Table>
                <thead>
                    <tr>
                        <TableHeader className="hidden sm:table-cell">{intl.formatMessage({id: 'list.settlement.header.state'})}</TableHeader>
                        <TableHeader className="hidden sm:table-cell">{intl.formatMessage({id: 'list.settlement.header.cutOffDate'})}</TableHeader>
                        <TableHeader>{intl.formatMessage({id: 'list.settlement.header.customer'})}</TableHeader>
                        <TableHeader>{intl.formatMessage({id: 'list.settlement.header.service'})}</TableHeader>
                        <TableHeader>{intl.formatMessage({id: 'list.settlement.header.description'})}</TableHeader>
                        <TableHeader textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'list.settlement.header.netTotal'})}</TableHeader>
                    </tr>
                </thead>
                <tbody>
                    {data.settlements.edges.map(
                        (edge, index) => {
                            const settlement = edge.node;

                            if (settlement === null || settlement === undefined) {
                                return (<></>);
                            }

                            const projectOrSubscriptionName = settlement.service.__typename === 'Subscription' ? settlement.service.subscriptionService.name : settlement.service.projectName
                            const totalNetAmount = Dinero({amount: settlement.totalNetAmount})

                            return (
                                <tr key={index} className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' hover:bg-gray-50 cursor-pointer group'} onClick={() => {
                                    navigate("/settlements/" + settlement.id + "/en")
                                }}>
                                    <TableData className="hidden sm:table-cell">
                                        <StatusBadge
                                            value={settlement.status}
                                            label={getSettlementStatusLabel(settlement.status)}
                                            states={{green: ['INVOICED'], yellow: ['FINALIZED', 'OPEN']}}
                                        />
                                    </TableData>
                                    <TableData className="hidden sm:table-cell"><FormattedDate value={edge.node.cutOffAt} day="2-digit" month="2-digit" year="numeric"/></TableData>
                                    <TableData>{settlement.customerAccount.customerName}</TableData>
                                    <TableData>{projectOrSubscriptionName}</TableData>
                                    <TableData>{settlement.title.en}</TableData>
                                    <TableData textAlignment={TextAlignment.Right}>
                                        {totalNetAmount.toFormat()}
                                    </TableData>
                                </tr>
                            )
                        }
                    )}
                </tbody>
            </Table>
            {hasNext && <LoadMoreButton onClick={() => loadNext(100)} isLoadingNext={isLoadingNext}/>}
        </nav>
    )
}

export default SettlementList;
